import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";

import { useAuthContext } from "@/features/auth/contexts/auth-context";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddUsuarioMutation } from "@/features/administracion/usuarios/api/add-usuario";
import { DeleteUsuarioMutation } from "@/features/administracion/usuarios/api/delete-usuario";
import { EditUsuarioMutation } from "@/features/administracion/usuarios/api/edit-usuario";
import { EditUsuarioPasswordMutation } from "@/features/administracion/usuarios/api/edit-usuario-password";
import { EditPermisosMutation } from "@/features/administracion/usuarios/api/edit-usuario-permisos";
import { getAllUsuarios, useGetAllUsuariosQuery } from "@/features/administracion/usuarios/api/get-all-usuarios";
import { GetUsuarioQuery } from "@/features/administracion/usuarios/api/get-usuario";
import { GetUsuarioPermisosQuery } from "@/features/administracion/usuarios/api/get-usuario-permisos";
import AddUsuarioForm, { addUsuarioFormSchema } from "@/features/administracion/usuarios/components/add-usuario-form";
import EditUsuarioForm, { editUsuarioFormSchema } from "@/features/administracion/usuarios/components/edit-usuario-form";
import EditUsuarioPasswordForm, { editUsuarioPasswordFormSchema } from "@/features/administracion/usuarios/components/edit-usuario-password-form";
import EditUsuarioPermisosForm, { editUsuarioPermisosFormSchema } from "@/features/administracion/usuarios/components/edit-usuario-permisos-form";
import { PermisosListToMap } from "@/features/administracion/usuarios/shared/utils";
import { ParamsProvider } from "@/features/configuration/contexts/params-context";

import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { Usuario } from "@/shared/types/usuario";

import ChecklistIcon from '@mui/icons-material/Checklist';
import PasswordIcon from '@mui/icons-material/Password';
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";

export default function UsuariosPage() {
    const openModal = useOpenModal();

    //Permisos
    const { hasPermisos } = useAuthContext();
    const hasManagePermisosPermiso = hasPermisos(Permiso.ManagePermisos);
    const hasManageOrganizacionesPermiso = hasPermisos(Permiso.ManageOrganizaciones);
    const hasManageUsuariosPermiso = hasPermisos(Permiso.ManageUsuarios);
    const canAssignRoles = hasManageUsuariosPermiso || hasManageOrganizacionesPermiso;

    //Fetch
    const filterData = useFilters({
        filters: [
            { type: "string", name: "nombre", label: "Nombre" },
            { type: "string", name: "numDocumento", label: "N° Documento" },
            { type: "string", name: "email", label: "Email" },
            { type: "string", name: "telefono", label: "Teléfono" },
            { type: "pagination" }
        ]
    })

    const fetchData = useGetAllUsuariosQuery(filterData.filterData);

    const columnData = useColumns({
        key: "usuarios",
        columns: [
            { key: "documento", header: "N° Documento", render: row => formatString(row.numDocumento) },
            { key: "nombre", header: "Nombre", render: row => formatString(row.nombre) },
            { key: "organizacion", header: "Organización", render: row => formatString(row.organizacionNombre) },
            { key: "email", header: "Email", render: row => formatString(row.email) },
            { key: "telefono", header: "Teléfono", render: row => formatString(row.telefono) },
            { key: "direccion", header: "Dirección", render: row => formatString(row.direccion), show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ]
    })

    //Mutation
    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Usuarios">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    { header: "Acción", width: "auto", align: "center", render: row => <UsuariosEntryOption row={row} /> }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        {/* Modales */}
        <Drawer code="add">
            <ModalHeader>Agregar Usuario</ModalHeader>
            <AddUsuarioMutation>
                {mutation => <SchemaFormHook
                    schema={addUsuarioFormSchema}
                    onSubmit={async ({ value }) => {
                        // Preparar datos según permisos del usuario
                        const requestData = {
                            ...value,
                            // Si no tiene permiso ManageOrganizaciones, no enviar organizacionId
                            organizacionId: hasManageOrganizacionesPermiso ? value.organizacionId : undefined,
                            // Si no puede asignar roles, no enviar el campo rol
                            rol: canAssignRoles ? value.rol : undefined,
                        };
                        await mutation.mutateAsync(requestData);
                        notify(`El usuario se ha agregado correctamente.`, { variant: "success" });
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <AddUsuarioForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddUsuarioMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Usuario</ModalHeader>
            <ModalValueExtractor>
                {usuarioId => <GetUsuarioQuery id={usuarioId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {usuario => <EditUsuarioMutation id={usuario.id}>
                            {mutation => <SchemaFormHook
                                schema={editUsuarioFormSchema}
                                onSubmit={async ({ value }) => {
                                    // Si el usuario no tiene permiso ManageOrganizaciones, no enviar organizacionId
                                    const requestData = hasManageOrganizacionesPermiso
                                        ? value
                                        : { ...value, organizacionId: undefined };
                                    await mutation.mutateAsync(requestData);
                                    notify(`El usuario se ha agregado correctamente.`, { variant: "success" });
                                }}
                                defaultValues={{ ...usuario }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <EditUsuarioForm form={form} margin={3} />}
                            </SchemaFormHook>}
                        </EditUsuarioMutation>}
                    </DataFetcher>}
                </GetUsuarioQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {usuario => <DeleteUsuarioMutation id={usuario.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Usuario "${usuario.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el usuario "${usuario.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El usuario "${usuario.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteUsuarioMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <Drawer code="editPassword">
            <ModalHeader>Cambiar Contraseña</ModalHeader>
            <ModalValueExtractor>
                {usuarioId => <EditUsuarioPasswordMutation id={usuarioId}>
                    {mutation => <SchemaFormHook
                        schema={editUsuarioPasswordFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value.newPassword);
                            notify(`La contraseña se ha editado correctamente.`, { variant: "success" });
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <EditUsuarioPasswordForm form={form} margin={3} />}
                    </SchemaFormHook>}
                </EditUsuarioPasswordMutation>}
            </ModalValueExtractor>
        </Drawer>

        {hasManagePermisosPermiso && <Drawer code="editPermisos">
            <ModalHeader>Editar Permisos</ModalHeader>
            <ModalValueExtractor<{ id: string, organizacionId: string }>>
                {({ id: usuarioId, organizacionId }) =>
                    <ParamsProvider organizacionId={organizacionId}>
                        <GetUsuarioPermisosQuery usuarioId={usuarioId}>
                            {query => <DataFetcher flex={1} query={query}>
                                {permisos => <EditPermisosMutation usuarioId={usuarioId}>
                                    {mutation => <SchemaFormHook
                                        schema={editUsuarioPermisosFormSchema}
                                        onSubmit={async ({ value }) => {
                                            await mutation.mutateAsync(value)
                                            notify(`Se han editado los permisos correctamente.`, { variant: "success" });
                                        }}
                                        defaultValues={{
                                            permisos: PermisosListToMap(permisos)
                                        }}
                                        submitOnce
                                        closeModalOnSubmit
                                    >
                                        {form => <EditUsuarioPermisosForm form={form} margin={3} />}
                                    </SchemaFormHook>}
                                </EditPermisosMutation>}
                            </DataFetcher>}
                        </GetUsuarioPermisosQuery>
                    </ParamsProvider>}
            </ModalValueExtractor>
        </Drawer>}

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllUsuarios}
            defaultFileTitle="Usuarios"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllUsuarios}
            defaultFileTitle="Usuarios"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllUsuarios}
            defaultFileTitle="Usuarios"
        />
    </ListPage>
}

function UsuariosEntryOption({ row }: { row: Usuario }) {
    const openModal = useOpenModal()

    //Auth
    const { authData, hasPermisos } = useAuthContext();
    const isSelf = authData.id === row.id;
    const hasPermiso = hasPermisos(Permiso.ManagePermisos);

    return <DataTable.EntryOption>
        <DataTable.EntryOption.EditButton
            onClick={() => openModal("edit", row.id)}
        />
        {!isSelf && <>
            {hasPermiso && <DataTable.EntryOption.Button
                title="Permisos" Icon={ChecklistIcon}
                onClick={() => openModal("editPermisos", { id: row.id, organizacionId: row.organizacionId })}
            />}
            <DataTable.EntryOption.Button
                title="Cambiar Contraseña" Icon={PasswordIcon}
                onClick={() => openModal("editPassword", row.id)}
            />
            <DataTable.EntryOption.DeleteButton
                disabled={row.protegido}
                onClick={() => openModal("delete", row)}
            />
        </>}
    </DataTable.EntryOption>
}
